import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { License, LicenseType, Transaction } from '../types/database';
import { useAuth } from './useAuth';
import { isLicenseValid } from '../utils/license-utils';

export function useLicenses() {
  const { user } = useAuth();
  const [licenses, setLicenses] = useState<License[]>([]);
  const [licenseTypes, setLicenseTypes] = useState<LicenseType[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);

  const loadUserLicenses = useCallback(async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from('licenses')
      .select(`
        *,
        license_types (*)
      `)
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (!error && data) {
      setLicenses(data);
    }
  }, [user]);

  const loadUserTransactions = useCallback(async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from('transactions')
      .select(`
        *,
        licenses (
          *,
          license_types (*)
        )
      `)
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (!error && data) {
      setTransactions(data);
    }
  }, [user]);

  const loadUserData = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    try {
      await Promise.all([
        loadUserLicenses(),
        loadUserTransactions(),
      ]);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [user, loadUserLicenses, loadUserTransactions]);

  useEffect(() => {
    if (user) {
      loadUserData();
    } else {
      setLicenses([]);
      setTransactions([]);
      setLoading(false);
    }
  }, [user, loadUserData]);

  useEffect(() => {
    loadLicenseTypes();
  }, []);

  const loadLicenseTypes = async () => {
    const { data, error } = await supabase
      .from('license_types')
      .select('*')
      .eq('is_active', true)
      .order('price', { ascending: true });

    if (error) {
    } else {
      setLicenseTypes(data || []);
    }
  };

  const checkLicenseValidity = isLicenseValid;

  const getActiveLicenses = (): License[] => {
    return licenses.filter(isLicenseValid);
  };

  return {
    licenses,
    licenseTypes,
    transactions,
    loading,
    checkLicenseValidity,
    getActiveLicenses,
    refreshData: loadUserData,
  };
}
